import React, { useEffect, useMemo, useRef, useState } from "react";
import { Routes, Route, Navigate, NavLink, useLocation, useParams } from "react-router-dom";
import {
  FaHome,
  FaBuilding,
  FaUsers,
  FaShareAlt,
  FaAddressBook,
  FaFolderOpen,
  FaBriefcase,
  FaChartLine,
  FaUserFriends,
  FaHeadset,
  FaSignOutAlt,
  FaBars,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaCog,
  FaBullhorn,
  FaGlobe,
  FaExchangeAlt,
  FaChevronDown,
  FaChevronUp,
  FaFileInvoiceDollar,
  FaClipboardList,
  FaCalendarAlt,
  FaClipboardCheck,
  FaInbox,
  FaUsersCog,
  FaSitemap,
  FaTasks,
  FaComments,
  FaWhatsapp,
  FaFacebookMessenger,
  FaListUl,
  FaUser,
  FaFileInvoice,
  FaHandHoldingUsd,
  FaWallet,
  FaSlidersH,
} from "react-icons/fa";
import "../assets/css/CustomerPortal.css";
import { FINANCE_SECTIONS, PORTAL_PAYABLES_HINT } from "../utils/financeTerms";
import axiosInstance from "../components/api/axiosInstance";
import TenantDashboard from "./TenantDashboard";
import TenantLeads from "./TenantLeads";
import RegisterLead from "./RegisterLead";
import TenantLeadView from "./TenantLeadView";
import TenantWebsiteLeads from "./TenantWebsiteLeads";
import TenantDigixLeads from "./TenantDigixLeads";
import TenantProfileView from "./TenantProfileView";
import TenantProfileEdit from "./TenantProfileEdit";
import TenantProfileLayout from "./TenantProfileLayout";
import TenantChangePassword from "./TenantChangePassword";
import TenantCompanyProfile from "./TenantCompanyProfile";
import TenantEmployees from "./TenantEmployees";
import TenantEmployeeView from "./TenantEmployeeView";
import TenantSocialAccounts from "./TenantSocialAccounts";
import TenantSocialAccountView from "./TenantSocialAccountView";
import TenantCustomers from "./TenantCustomers";
import TenantCustomerView from "./TenantCustomerView";
import TenantClients from "./TenantClients";
import TenantClientView from "./TenantClientView";
import TenantProjects from "./TenantProjects";
import TenantServices from "./TenantServices";
import TenantSupport from "./TenantSupport";
import TenantSettings from "./TenantSettings";
import DMProjects from "../pages/dm/DMProjects";
import DMProjectWorkspace from "../pages/dm/DMProjectWorkspace";
import { portalDmApi } from "../pages/dm/dmApi";
import TenantDmProjectNew from "./TenantDmProjectNew";
import TenantDmSetup from "./TenantDmSetup";
import DigitalMarketingPage from "../pages/DigitalMarketingPage";
import TenantFinance from "./TenantFinance";
import TenantInvoiceView from "./TenantInvoiceView";
import TenantReceivables from "./billing/TenantReceivables";
import TenantInvoices from "./billing/TenantInvoices";
import TenantFinanceSettings from "./billing/TenantFinanceSettings";
import TenantPaymentsReceived from "./billing/TenantPaymentsReceived";
import TenantClientInvoiceEditor from "./billing/TenantClientInvoiceEditor";
import TenantClientInvoiceView from "./billing/TenantClientInvoiceView";
import PortalNoticeBanner from "../components/PortalNoticeBanner";
import MyWork from "../pages/work/MyWork";
import WorkFollowUps from "../pages/work/WorkFollowUps";
import ReviewInbox from "../pages/work/ReviewInbox";
import TeamBoard from "../pages/work/TeamBoard";
import OrgSetup from "../pages/work/OrgSetup";
import OrgChart from "../pages/work/OrgChart";
import { WorkPlanPage, WorkProjects } from "../pages/work/WorkPlanPage";
import WhatsAppPage from "../pages/work/WhatsAppPage";
import MessagesPage from "../pages/work/MessagesPage";
import { CommentsPage } from "../pages/work/CommentsInbox";
import NotificationBell from "../pages/work/NotificationBell";
import { portalWorkApi } from "../pages/work/workApi";
import { currentPortalHost, resolvePortalHost } from "../context/PortalHostContext";
import { clearAuthSessionStorage } from "../utils/clearSessionStorage";
import {
  getSessionTenants,
  persistSignedInSession,
  setAuthItem,
  updateClientPortalProfile,
} from "../utils/authStorage";
import {
  COMPANY_LOGO_EVENT,
  getPortalModules,
  getPortalSession,
  isModuleEnabled,
  isPortalOwner,
  pickBusinessFields,
} from "./portalSession";
import { getCtxHomeUrl } from "../config/ctxPath";
import {
  getSidebarTheme,
  normalizeSidebarTheme,
  setSidebarTheme,
  sidebarThemeClassName,
} from "../utils/sidebarTheme";

/** Old links to a Cloud Office invoice (/portal/finance/invoices/:id) now live under Payables. */
function LegacyPayableInvoiceRedirect() {
  const { id } = useParams();
  return <Navigate to={`/portal/finance/payables/${id}`} replace />;
}

const NAV_ITEMS = [
  { to: "/portal", end: true, label: "Dashboard", icon: FaHome },
  {
    key: "work",
    label: "Work",
    icon: FaTasks,
    children: [
      { to: "/portal/my-work", label: "My Work", icon: FaClipboardCheck, module: "work" },
      { to: "/portal/work/follow-ups", label: "Follow-ups", icon: FaListUl, module: "work", workPage: "follow_ups" },
      { to: "/portal/work/review", label: "Review Inbox", icon: FaInbox, module: "work", workPage: "review" },
      { to: "/portal/work/team", label: "Team Board", icon: FaUsersCog, module: "work", workPage: "team" },
      { to: "/portal/work/projects", label: "Plan & Assign", icon: FaSitemap, module: "work", workPage: "plan" },
    ],
  },
  { to: "/portal/company-profile", label: "Company Profile", icon: FaBuilding, ownerOnly: true },
  {
    key: "employees",
    label: "Employees",
    icon: FaUsers,
    children: [
      { to: "/portal/employees", label: "All Employees", icon: FaUsers, ownerOnly: true, module: "employees" },
      { to: "/portal/org-chart", label: "Org Chart", icon: FaSitemap, ownerOnly: true, module: "employees" },
    ],
  },
  { to: "/portal/clients", label: "Clients", icon: FaAddressBook, module: "clients" },
  { to: "/portal/projects", label: "Projects", icon: FaFolderOpen, module: "projects" },
  { to: "/portal/services", label: "Services", icon: FaBriefcase, module: "services" },
  {
    key: "digital-marketing",
    label: "Digital Marketing",
    icon: FaBullhorn,
    children: [
      { to: "/portal/dm-setup", label: "Setup", icon: FaTasks, module: "digital_marketing" },
      { to: "/portal/social-accounts", label: "Social Accounts", icon: FaShareAlt, module: "social_accounts" },
      {
        to: "/portal/digital-marketing",
        label: "Marketing Projects",
        soloLabel: "Digital Marketing",
        icon: FaClipboardList,
        soloIcon: FaBullhorn,
        module: "digital_marketing",
      },
      { to: "/portal/dm-calendar", label: "Calendar", icon: FaCalendarAlt, module: "digital_marketing" },
      { to: "/portal/work/comments", label: "Comments", icon: FaComments, module: "digital_marketing" },
      { to: "/portal/work/messages", label: "Messages", icon: FaFacebookMessenger, module: "digital_marketing" },
      { to: "/portal/work/whatsapp", label: "WhatsApp", icon: FaWhatsapp, module: "digital_marketing" },
      { to: "/portal/website-leads", label: "Website Leads", icon: FaGlobe, module: "leads" },
    ],
  },
  { to: "/portal/leads", label: "Leads", icon: FaChartLine, module: "leads" },
  { to: "/portal/digix-leads", label: "Digix Leads", icon: FaInbox, module: "leads" },
  { to: "/portal/customers", label: "Customers", icon: FaUserFriends, module: "customers" },
  {
    key: "finance",
    label: "Finance",
    icon: FaFileInvoiceDollar,
    children: [
      { to: "/portal/finance/invoices", label: FINANCE_SECTIONS.invoices.label, hint: FINANCE_SECTIONS.invoices.hint, icon: FaFileInvoice, module: "finance" },
      { to: "/portal/finance/receivables", label: FINANCE_SECTIONS.receivables.label, hint: FINANCE_SECTIONS.receivables.hint, icon: FaFileInvoiceDollar, module: "finance" },
      { to: "/portal/finance/payments-received", label: FINANCE_SECTIONS.paymentsReceived.label, hint: FINANCE_SECTIONS.paymentsReceived.hint, icon: FaHandHoldingUsd, module: "finance" },
      { to: "/portal/finance/payables", label: FINANCE_SECTIONS.payables.label, hint: PORTAL_PAYABLES_HINT, icon: FaWallet, module: "finance" },
      { to: "/portal/finance/settings", label: FINANCE_SECTIONS.settings.label, hint: FINANCE_SECTIONS.settings.hint, icon: FaSlidersH, module: "finance" },
    ],
  },
];

// Sidebar groups: one open at a time (Work at first); the choice is kept per browser.
const NAV_GROUP_STORAGE_KEY = "cportalNavGroupOpen_v2";
const DEFAULT_OPEN_NAV_GROUP = "work";

function readOpenNavGroup() {
  try {
    const raw = localStorage.getItem(NAV_GROUP_STORAGE_KEY);
    return raw === null ? DEFAULT_OPEN_NAV_GROUP : raw;
  } catch (_) {
    return DEFAULT_OPEN_NAV_GROUP;
  }
}

function getCustomer() {
  return getPortalSession();
}

function companyInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "C";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
}

function formatTitleCase(value) {
  return String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function TenantPortal() {
  const location = useLocation();
  const customer = useMemo(getCustomer, []);
  const portalOwner = isPortalOwner(customer);
  const [modules, setModules] = useState(() => getPortalModules(customer));
  // Work pages this member's roles open (Permissions page); null while loading.
  const [workPages, setWorkPages] = useState(null);
  useEffect(() => {
    let alive = true;
    portalWorkApi
      .access()
      .then((res) => alive && setWorkPages(res?.pages || []))
      .catch(() => alive && setWorkPages([]));
    return () => {
      alive = false;
    };
  }, []);
  const canWork = (page) => !page || (workPages || []).includes(page);
  // Work pages need the Work Management service (module "work") and, except My Work, the role's page.
  const workRoute = (page, element) => {
    if (modules === null || workPages === null) return null;
    if (!isModuleEnabled(modules, "work")) return <Navigate to="/portal" replace />;
    return canWork(page) ? element : <Navigate to="/portal/my-work" replace />;
  };
  const navItems = useMemo(() => {
    const allowed = (item) =>
      (portalOwner || !item.ownerOnly) &&
      isModuleEnabled(modules, item.module) &&
      (!item.workPage || (workPages || []).includes(item.workPage));
    return NAV_ITEMS.flatMap((item) => {
      if (!item.children) return allowed(item) ? [item] : [];
      const children = item.children.filter(allowed);
      if (children.length === 0) return [];
      if (children.length === 1) {
        const [only] = children;
        return [{ ...only, label: only.soloLabel || only.label, icon: only.soloIcon || only.icon }];
      }
      return [{ ...item, children }];
    });
  }, [portalOwner, modules, workPages]);
  const isPathActive = (to) => location.pathname === to || location.pathname.startsWith(`${to}/`);
  const [openGroup, setOpenGroup] = useState(readOpenNavGroup);
  const saveOpenGroup = (next) => {
    try {
      localStorage.setItem(NAV_GROUP_STORAGE_KEY, next);
    } catch (_) {
      /* ignore */
    }
    return next;
  };
  const toggleGroup = (key) => setOpenGroup((prev) => saveOpenGroup(prev === key ? "" : key));
  // Opening a page inside a collapsed group (link, bookmark) expands that group and collapses the rest.
  useEffect(() => {
    const group = navItems.find(
      (item) =>
        item.children &&
        item.children.some((child) => location.pathname === child.to || location.pathname.startsWith(`${child.to}/`))
    );
    if (!group) return;
    setOpenGroup((prev) => (prev === group.key ? prev : saveOpenGroup(group.key)));
  }, [location.pathname, navItems]);
  const moduleRoute = (module, element) => {
    if (modules === null) return null;
    return isModuleEnabled(modules, module) ? element : <Navigate to="/portal" replace />;
  };
  const ownerModuleRoute = (module, element) =>
    portalOwner ? moduleRoute(module, element) : <Navigate to="/portal" replace />;
  const [companyName, setCompanyName] = useState(customer.Company_Name || "");
  const [companyLogo, setCompanyLogo] = useState(null);
  const [businesses, setBusinesses] = useState(getSessionTenants);
  const [switching, setSwitching] = useState(false);
  const [businessMenuOpen, setBusinessMenuOpen] = useState(false);
  const currentTenantId = customer.Tenant_Id ?? customer.client_id;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("cportal_sidebar_collapsed") === "1";
    } catch (_) {
      return false;
    }
  });
  const [sidebarTheme, setSidebarThemeState] = useState(
    () => normalizeSidebarTheme(customer.Sidebar_Theme, "portal") || getSidebarTheme("portal")
  );

  useEffect(() => {
    const fromAccount = normalizeSidebarTheme(customer.Sidebar_Theme, "portal");
    if (fromAccount && fromAccount !== getSidebarTheme("portal")) {
      setSidebarTheme("portal", fromAccount);
    }
  }, [customer]);

  useEffect(() => {
    const sync = (e) => {
      if (e?.detail?.scope === "portal" && e.detail.theme) {
        setSidebarThemeState(e.detail.theme);
        return;
      }
      setSidebarThemeState(getSidebarTheme("portal"));
    };
    window.addEventListener("sidebarThemeChanged", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("sidebarThemeChanged", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/client/company");
        const company = res.data?.data || {};
        if (!active) return;
        if (company.Company_Name) setCompanyName(String(company.Company_Name).trim());
        setCompanyLogo(company.Logo_Url || null);
        updateClientPortalProfile({ ...getPortalSession(), ...pickBusinessFields(company) });
        if (Array.isArray(company.Enabled_Modules)) {
          setModules(company.Enabled_Modules);
          updateClientPortalProfile({ ...getPortalSession(), Enabled_Modules: company.Enabled_Modules });
        } else {
          setModules((prev) => prev ?? []);
        }
      } catch (_) {
        // Keep company name from login session; without a module list only core items stay visible.
        if (active) setModules((prev) => prev ?? []);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onLogoChange = (e) => setCompanyLogo(e.detail || null);
    window.addEventListener(COMPANY_LOGO_EVENT, onLogoChange);
    return () => window.removeEventListener(COMPANY_LOGO_EVENT, onLogoChange);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get("/my-tenants");
        const list = res.data?.data?.tenants;
        if (active && Array.isArray(list)) {
          setBusinesses(list);
          setAuthItem("tenants", JSON.stringify(list));
        }
      } catch (_) {
        // Keep the list from the sign-in response.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  /** A company on the same person's other login (own domain): open it signed in, no password. */
  const openLinkedLoginCompany = async (choice) => {
    const signInThere = () => {
      if (!choice.Portal_URL) {
        window.alert(`Sign in to ${choice.Company_Name || "this company"} with ${choice.Login_Email}.`);
        return;
      }
      const url = new URL(choice.Portal_URL, window.location.origin);
      if (choice.Login_Email) url.searchParams.set("email", choice.Login_Email);
      window.location.href = url.toString();
    };
    try {
      const res = await axiosInstance.post("/linked-login-handoff", { tenant_id: choice.Tenant_Id });
      const handoff = res.data?.data;
      if (res.data?.status !== "success" || !handoff?.token) {
        signInThere();
        return;
      }
      const target = await resolvePortalHost();
      const otherHost =
        target?.enforced &&
        target.kind !== "dev" &&
        handoff.portal_url &&
        handoff.portal_host !== currentPortalHost();
      if (otherHost) {
        const url = new URL(handoff.portal_url);
        url.searchParams.set("handoff", handoff.token);
        window.location.href = url.toString();
        return;
      }
      const exchange = await axiosInstance.post("/linked-login-handoff/exchange", {
        token: handoff.token,
        portal_host: currentPortalHost(),
      });
      if (exchange.data?.status === "success" && exchange.data.data) {
        persistSignedInSession(exchange.data.data);
        window.location.href = getCtxHomeUrl();
        return;
      }
      signInThere();
    } catch (_) {
      signInThere();
    }
  };

  const handleSwitchBusiness = async (value) => {
    const tenantId = Number(value);
    if (!tenantId || tenantId === Number(currentTenantId)) return;
    const choice = businesses.find((b) => Number(b.Tenant_Id) === tenantId);
    if (choice?.Requires_Sign_In) {
      setSwitching(true);
      await openLinkedLoginCompany(choice);
      setSwitching(false);
      return;
    }
    setSwitching(true);
    try {
      const res = await axiosInstance.post("/switch-tenant", { tenant_id: tenantId });
      if (res.data?.status === "success" && res.data.data) {
        const portal = res.data.data.portal;
        const target = await resolvePortalHost();
        // Once hosts are enforced, a company on another portal host is opened there (sign in again).
        if (portal?.host_mismatch && portal.url && target?.enforced && target.kind !== "dev") {
          window.location.href = portal.url;
          return;
        }
        persistSignedInSession(res.data.data);
        window.location.href = getCtxHomeUrl();
        return;
      }
      window.alert(res.data?.message || "Could not switch company.");
    } catch (err) {
      window.alert(err.response?.data?.message || "Could not switch company.");
    }
    setSwitching(false);
  };

  useEffect(() => {
    setSidebarOpen(false);
    setAccountMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const onDoc = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [accountMenuOpen]);

  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [sidebarOpen]);

  const brandName = formatTitleCase(companyName.trim() || "Cloud Connect");
  const brandInitial = companyInitials(brandName);
  // Left nav shows first and last name only; the middle name appears on profile screens.
  const displayName =
    `${customer.First_Name || ""} ${customer.Last_Name || ""}`.trim() || customer.Full_Name || "Client";
  const userEmail = customer.Email || customer.email || "";
  const initial = (displayName || "C").charAt(0).toUpperCase();
  const accountActive = ["/portal/profile", "/portal/settings", "/portal/support"].some(isPathActive);

  const handleLogout = () => {
    clearAuthSessionStorage();
    window.location.href = getCtxHomeUrl();
  };

  const closeMenus = () => {
    setAccountMenuOpen(false);
    setSidebarOpen(false);
  };

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem("cportal_sidebar_collapsed", next ? "1" : "0");
      } catch (_) {}
      return next;
    });
  };

  return (
    <div
      className={`cportal${sidebarOpen ? " cportal-sidebar-open" : ""}${
        sidebarCollapsed ? " cportal-sidebar-collapsed" : ""
      } ${sidebarThemeClassName(sidebarTheme)}`}
    >
      {sidebarOpen && (
        <button
          type="button"
          className="cportal-sidebar-backdrop"
          aria-label="Close navigation menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className="cportal-sidebar">
        <div className="cportal-sidebar-brand">
          <span className={`cportal-brand-mark${companyLogo ? " has-logo" : ""}`} title={brandName}>
            {companyLogo ? (
              <img
                src={companyLogo}
                alt=""
                className="cportal-brand-logo"
                onError={() => setCompanyLogo(null)}
              />
            ) : (
              brandInitial
            )}
          </span>
          <span className="cportal-brand-text" title={brandName}>
            {brandName}
          </span>
          <NotificationBell api={portalWorkApi} />
        </div>

        <nav className="cportal-nav">
          {navItems.map(({ key, to, end, label, icon: Icon, children }) => {
            if (!children) {
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className="cportal-nav-link"
                  title={label}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="cportal-nav-link-icon" aria-hidden="true" />
                  <span className="cportal-nav-link-label">{label}</span>
                </NavLink>
              );
            }
            const groupActive = children.some((child) => isPathActive(child.to));
            const groupOpen = openGroup === key;
            const headId = `cportal-nav-group-${key}`;
            return (
              <div
                key={key}
                className={`cportal-nav-group${groupActive ? " is-active" : ""}`}
                role="group"
                aria-labelledby={headId}
              >
                <button
                  type="button"
                  id={headId}
                  className="cportal-nav-link cportal-nav-group-head"
                  title={label}
                  aria-expanded={groupOpen}
                  aria-controls={`${headId}-items`}
                  onClick={() => toggleGroup(key)}
                >
                  <Icon className="cportal-nav-link-icon" aria-hidden="true" />
                  <span className="cportal-nav-link-label">{label}</span>
                  {groupOpen ? (
                    <FaChevronUp className="cportal-nav-group-chevron" aria-hidden="true" />
                  ) : (
                    <FaChevronDown className="cportal-nav-group-chevron" aria-hidden="true" />
                  )}
                </button>
                <div className="cportal-nav-sub" id={`${headId}-items`} hidden={!groupOpen && !sidebarCollapsed}>
                  {children.map((child) => {
                    const ChildIcon = child.icon;
                    return (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end={child.end}
                        className="cportal-nav-link"
                        title={child.hint ? `${child.label}: ${child.hint}` : child.label}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <ChildIcon className="cportal-nav-link-icon" aria-hidden="true" />
                        <span className="cportal-nav-link-label">{child.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="cportal-sidebar-footer">
          {businesses.length > 1 && (
            <>
              <div
                className="cportal-sidebar-link-divider cportal-business-switch-divider"
                aria-hidden="true"
              />
              <div className="cportal-business-switch">
                <button
                  type="button"
                  className="cportal-nav-link cportal-footer-link cportal-business-toggle"
                  aria-expanded={businessMenuOpen}
                  aria-controls="cportal-business-list"
                  title="Switch Business"
                  onClick={() => setBusinessMenuOpen((open) => !open)}
                  disabled={switching}
                >
                  <FaExchangeAlt className="cportal-nav-link-icon" aria-hidden="true" />
                  <span className="cportal-nav-link-label">Switch Business</span>
                  {businessMenuOpen ? (
                    <FaChevronUp className="cportal-business-toggle-chevron" aria-hidden="true" />
                  ) : (
                    <FaChevronDown className="cportal-business-toggle-chevron" aria-hidden="true" />
                  )}
                </button>
                {businessMenuOpen && (
                  <ul id="cportal-business-list" className="cportal-business-list">
                    {businesses
                      .filter((b) => Number(b.Tenant_Id) !== Number(currentTenantId))
                      .map((b) => {
                        const note = b.Is_Platform
                          ? "Tumma staff"
                          : b.Requires_Sign_In
                            ? b.Login_Email || "Own login"
                            : b.scope === "owner"
                              ? "Owner"
                              : "";
                        return (
                          <li key={b.Tenant_Id}>
                            <button
                              type="button"
                              className="cportal-business-option"
                              title={b.Company_Name || `Company #${b.Tenant_Id}`}
                              onClick={() => handleSwitchBusiness(b.Tenant_Id)}
                              disabled={switching}
                            >
                              <span className="cportal-business-option-name">
                                {b.Company_Name || `Company #${b.Tenant_Id}`}
                              </span>
                              {note && <span className="cportal-business-option-note">{note}</span>}
                            </button>
                          </li>
                        );
                      })}
                  </ul>
                )}
              </div>
            </>
          )}

          <div className="cportal-sidebar-footer-divider">
            <button
              type="button"
              className="cportal-sidebar-collapse-btn"
              onClick={toggleSidebarCollapse}
              aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {sidebarCollapsed ? (
                <FaAngleDoubleRight size={13} aria-hidden="true" />
              ) : (
                <FaAngleDoubleLeft size={13} aria-hidden="true" />
              )}
            </button>
          </div>

          <div ref={accountMenuRef}>
            {accountMenuOpen && (
              <div
                id="cportal-account-menu"
                className="cportal-sidebar-footer-links cportal-account-menu"
                role="menu"
                aria-label="Account menu"
              >
                <NavLink
                  to="/portal/profile"
                  className="cportal-nav-link cportal-footer-link"
                  role="menuitem"
                  title="Profile"
                  onClick={closeMenus}
                >
                  <FaUser className="cportal-nav-link-icon" aria-hidden="true" />
                  <span className="cportal-nav-link-label">Profile</span>
                </NavLink>
                <NavLink
                  to="/portal/settings"
                  className="cportal-nav-link cportal-footer-link"
                  role="menuitem"
                  title="Settings"
                  onClick={closeMenus}
                >
                  <FaCog className="cportal-nav-link-icon" aria-hidden="true" />
                  <span className="cportal-nav-link-label">Settings</span>
                </NavLink>
                <NavLink
                  to="/portal/support"
                  className="cportal-nav-link cportal-footer-link"
                  role="menuitem"
                  title="Support"
                  onClick={closeMenus}
                >
                  <FaHeadset className="cportal-nav-link-icon" aria-hidden="true" />
                  <span className="cportal-nav-link-label">Support</span>
                </NavLink>
                <button
                  type="button"
                  className="cportal-nav-link cportal-footer-link cportal-footer-link--signout"
                  role="menuitem"
                  title="Sign out"
                  onClick={handleLogout}
                >
                  <FaSignOutAlt className="cportal-nav-link-icon" aria-hidden="true" />
                  <span className="cportal-nav-link-label">Sign out</span>
                </button>
              </div>
            )}

            <button
              type="button"
              className={`cportal-account-trigger${accountMenuOpen || accountActive ? " is-active" : ""}`}
              onClick={() => setAccountMenuOpen((open) => !open)}
              aria-expanded={accountMenuOpen}
              aria-haspopup="menu"
              aria-controls="cportal-account-menu"
              title={displayName}
            >
              <span className="cportal-account-trigger-avatar" aria-hidden="true">
                {initial}
              </span>
              <span className="cportal-account-trigger-details">
                <span className="cportal-account-trigger-name">{displayName}</span>
                <span className="cportal-account-trigger-email">{userEmail || "—"}</span>
              </span>
              <FaChevronUp
                className={`cportal-account-trigger-caret${accountMenuOpen ? " open" : ""}`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </aside>

      <div className="cportal-main">
        <header className="cportal-mobile-bar">
          <span className="cportal-mobile-bar-title" title={brandName}>
            {companyLogo ? (
              <img
                src={companyLogo}
                alt=""
                className="cportal-mobile-bar-logo"
                onError={() => setCompanyLogo(null)}
              />
            ) : null}
            <span className="cportal-mobile-bar-name">{brandName}</span>
          </span>
          <button
            type="button"
            className="cportal-sidebar-toggle"
            onClick={() => setSidebarOpen((open) => !open)}
            aria-label={sidebarOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={sidebarOpen}
          >
            <FaBars aria-hidden="true" />
          </button>
        </header>

        <main className="cportal-content">
          <PortalNoticeBanner />
          <Routes>
            <Route path="/portal" element={<TenantDashboard modules={modules} />} />
            <Route path="/portal/my-work" element={workRoute(null, <MyWork api={portalWorkApi} />)} />
            <Route path="/portal/work/follow-ups" element={workRoute("follow_ups", <WorkFollowUps api={portalWorkApi} />)} />
            <Route path="/portal/work/review" element={workRoute("review", <ReviewInbox api={portalWorkApi} />)} />
            <Route path="/portal/work/team" element={workRoute("team", <TeamBoard api={portalWorkApi} />)} />
            <Route path="/portal/work/org" element={workRoute("team", <OrgSetup api={portalWorkApi} />)} />
            <Route path="/portal/work/calendar" element={<Navigate to="/portal/dm-calendar" replace />} />
            <Route path="/portal/work/projects" element={workRoute("plan", <WorkProjects api={portalWorkApi} />)} />
            <Route path="/portal/work/plan/:ptype/:id" element={workRoute(null, <WorkPlanPage api={portalWorkApi} />)} />
            <Route path="/portal/work/connections" element={<Navigate to="/portal/social-accounts" replace />} />
            <Route path="/portal/work/comments" element={moduleRoute("digital_marketing", <CommentsPage api={portalWorkApi} />)} />
            <Route path="/portal/work/whatsapp" element={moduleRoute("digital_marketing", <WhatsAppPage api={portalWorkApi} />)} />
            <Route path="/portal/work/messages" element={moduleRoute("digital_marketing", <MessagesPage api={portalWorkApi} />)} />
            <Route path="/portal/clients" element={moduleRoute("clients", <TenantClients />)} />
            <Route
              path="/portal/clients/new"
              element={moduleRoute("clients", <TenantClients createMode />)}
            />
            <Route
              path="/portal/clients/:id"
              element={moduleRoute("clients", <TenantClientView modules={modules} />)}
            />
            <Route path="/portal/projects" element={moduleRoute("projects", <TenantProjects />)} />
            <Route
              path="/portal/projects/new"
              element={moduleRoute("projects", <TenantProjects createMode />)}
            />
            <Route path="/portal/services" element={moduleRoute("services", <TenantServices />)} />
            <Route
              path="/portal/digital-marketing"
              element={moduleRoute(
                "digital_marketing",
                <DMProjects api={portalDmApi} basePath="/portal/digital-marketing" />
              )}
            />
            <Route
              path="/portal/digital-marketing/new"
              element={moduleRoute(
                "digital_marketing",
                <TenantDmProjectNew api={portalDmApi} basePath="/portal/digital-marketing" />
              )}
            />
            <Route
              path="/portal/digital-marketing/:id"
              element={moduleRoute(
                "digital_marketing",
                <DMProjectWorkspace api={portalDmApi} basePath="/portal/digital-marketing" />
              )}
            />
            <Route
              path="/portal/dm-calendar"
              element={moduleRoute("digital_marketing", <DigitalMarketingPage portal />)}
            />
            <Route path="/portal/dm-setup" element={moduleRoute("digital_marketing", <TenantDmSetup />)} />
            <Route path="/portal/leads" element={moduleRoute("leads", <TenantLeads mode="leads" />)} />
            <Route path="/portal/digix-leads" element={moduleRoute("leads", <TenantDigixLeads />)} />
            <Route
              path="/portal/website-leads"
              element={moduleRoute("leads", <TenantWebsiteLeads />)}
            />
            <Route path="/portal/customers" element={moduleRoute("customers", <TenantCustomers />)} />
            <Route
              path="/portal/customers/new"
              element={moduleRoute("customers", <TenantCustomers createMode />)}
            />
            <Route
              path="/portal/customers/:id"
              element={moduleRoute("customers", <TenantCustomerView />)}
            />
            <Route path="/portal/leads/new" element={moduleRoute("leads", <RegisterLead />)} />
            <Route path="/portal/leads/:id" element={moduleRoute("leads", <TenantLeadView />)} />
            <Route path="/portal/finance" element={<Navigate to="/portal/finance/payables" replace />} />
            <Route path="/portal/finance/payables" element={moduleRoute("finance", <TenantFinance />)} />
            <Route path="/portal/finance/payables/:id" element={moduleRoute("finance", <TenantInvoiceView />)} />
            <Route path="/portal/finance/invoices" element={moduleRoute("finance", <TenantInvoices />)} />
            <Route path="/portal/finance/invoices/:id" element={<LegacyPayableInvoiceRedirect />} />
            <Route path="/portal/finance/receivables" element={moduleRoute("finance", <TenantReceivables />)} />
            <Route path="/portal/finance/payments-received" element={moduleRoute("finance", <TenantPaymentsReceived />)} />
            <Route path="/portal/finance/settings" element={moduleRoute("finance", <TenantFinanceSettings />)} />
            <Route path="/portal/finance/client-invoices" element={<Navigate to="/portal/finance/invoices" replace />} />
            <Route path="/portal/finance/client-invoices/new" element={moduleRoute("finance", <TenantClientInvoiceEditor />)} />
            <Route path="/portal/finance/client-invoices/:id" element={moduleRoute("finance", <TenantClientInvoiceView />)} />
            <Route path="/portal/finance/client-invoices/:id/edit" element={moduleRoute("finance", <TenantClientInvoiceEditor />)} />
            <Route path="/portal/company-profile" element={portalOwner ? <TenantCompanyProfile /> : <Navigate to="/portal" replace />} />
            <Route path="/portal/employees" element={ownerModuleRoute("employees", <TenantEmployees />)} />
            <Route path="/portal/employees/new" element={ownerModuleRoute("employees", <TenantEmployees createMode />)} />
            <Route path="/portal/employees/:id" element={ownerModuleRoute("employees", <TenantEmployeeView />)} />
            <Route
              path="/portal/org-chart"
              element={ownerModuleRoute(
                "employees",
                <OrgChart
                  api={portalWorkApi}
                  profileLink={(id) => `/portal/employees/${id}`}
                  editLink={(id) => `/portal/employees/${id}`}
                />
              )}
            />
            <Route
              path="/portal/social-accounts"
              element={moduleRoute("social_accounts", <TenantSocialAccounts />)}
            />
            <Route
              path="/portal/social-accounts/new"
              element={moduleRoute("social_accounts", <TenantSocialAccounts createMode />)}
            />
            <Route
              path="/portal/social-accounts/:id"
              element={moduleRoute("social_accounts", <TenantSocialAccountView />)}
            />
            <Route path="/portal/support" element={<TenantSupport />} />
            <Route path="/portal/settings" element={<TenantSettings />} />
            <Route path="/portal/profile" element={<TenantProfileLayout />}>
              <Route index element={<TenantProfileView />} />
              <Route path="edit" element={<TenantProfileEdit />} />
              <Route path="change-password" element={<TenantChangePassword />} />
            </Route>
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default TenantPortal;
